import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Flex, Typography, Alert, Spin, message, Card, Tag, Avatar, QRCode } from 'antd';
import { jsPDF } from 'jspdf';
import {
  CheckCircleOutlined,
  DownloadOutlined,
  PrinterOutlined,
  UserOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import * as abhaApi from '../services/api';

const { Title, Paragraph, Text } = Typography;

// ── Helper to extract txnId from various API response formats ────────────────
export const extractTxnId = (res) => {
  if (!res) return null;
  const findTxnId = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    const keys = Object.keys(obj);
    // Priority keys
    const priority = ['txnid', 'transactionid', 'txn_id', 'transaction_id', 'txnId'];
    for (const p of priority) {
      const found = keys.find(k => k.toLowerCase() === p);
      if (found && (typeof obj[found] === 'string' || typeof obj[found] === 'number')) return obj[found];
    }
    // Fuzzy search
    const fuzzy = keys.find(k => k.toLowerCase().includes('txn') || k.toLowerCase().includes('transaction'));
    if (fuzzy && (typeof obj[fuzzy] === 'string' || typeof obj[fuzzy] === 'number')) return obj[fuzzy];
    // Recursive search
    for (const k of keys) {
      if (typeof obj[k] === 'object') {
        const res = findTxnId(obj[k]);
        if (res) return res;
      }
    }
    return null;
  };
  return findTxnId(res);
};

// ── Aggressive Token Search ──────────────────────────────────────────────────
export const findToken = (obj) => {
  if (!obj) return null;
  
  // 1. If input is a JWT string, return it immediately
  if (typeof obj === 'string' && obj.startsWith('ey') && obj.length > 20) return obj;

  const authKeys = [
    'token', 'accessToken', 'auth_token', 'jwtResponse', 'access_token', 
    'id_token', 'jwt', 'x-token', 'authorization',
    'txnid', 'txn_id', 'transactionid', 'transaction_id', 'txnId'
  ];
  const photoKeys = ['photo', 'profilephoto', 'image', 'picture', 'photo_data', 'bytea', 'byte', 'base64'];

  // Global search for JWTs (Starts with 'eyJ')
  // We prioritize JWTs anywhere in the object above anything else
  const searchForJwt = (current, depth = 0) => {
    if (depth > 8 || !current || typeof current !== 'object') return null;
    
    // Prioritize direct properties first at this level
    const keys = Object.keys(current);
    for (const key of keys) {
      const val = current[key];
      if (typeof val === 'string' && val.startsWith('ey') && val.length > 20) {
        return val;
      }
    }
    
    // Then recurse
    for (const key of keys) {
      const val = current[key];
      if (val && typeof val === 'object' && !photoKeys.some(pk => key.toLowerCase().includes(pk))) {
        const found = searchForJwt(val, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  const jwtToken = searchForJwt(obj);
  if (jwtToken) return jwtToken;

  // 2. If no JWT, search for specific "token" keys
  const searchForTokenKeys = (current, depth = 0) => {
    if (depth > 8 || !current || typeof current !== 'object') return null;
    
    const keys = Object.keys(current);
    // Priority check at this level
    for (const key of keys) {
      const k = key.toLowerCase();
      const val = current[key];
      
      // Ignore photo keys explicitly
      if (photoKeys.some(pk => k.includes(pk))) continue;
      
      if (typeof val === 'string' && val.length > 5 && authKeys.some(ak => ak.toLowerCase() === k)) {
        return val;
      }
    }
    
    // Fuzzy check at this level
    for (const key of keys) {
      const k = key.toLowerCase();
      const val = current[key];
      if (photoKeys.some(pk => k.includes(pk))) continue;
      
      if (typeof val === 'string' && val.length > 10 && (k.includes('token') || k.includes('jwt') || k.includes('auth'))) {
        return val;
      }
    }

    // Recurse
    for (const key of keys) {
      const val = current[key];
      if (val && typeof val === 'object' && !photoKeys.some(pk => key.toLowerCase().includes(pk))) {
        const found = searchForTokenKeys(val, depth + 1);
        if (found) return found;
      }
    }
    return null;
  };

  return searchForTokenKeys(obj);
};

// ── Helper to find profile data in API response ──────────────────────────────
export const findProfileData = (res) => {
  if (!res) return null;
  
  console.log('[ABHA DEBUG] Scanning response for profile/token');

  // If response is just a string, it might be the token itself
  if (typeof res === 'string' && res.length > 20) return { token: res };

  if (typeof res !== 'object') return null;
  
  // Extract token from anywhere in the response
  const globalToken = findToken(res) || extractTxnId(res);

  // If it's an array, it might be an accounts array directly
  if (Array.isArray(res)) {
    if (res.length > 0) return res[0]; 
    return null;
  }

  // 1. Identify primary profile base from accounts/users arrays
  let profile = null;
  const accounts = res.accounts || res.data?.accounts || res.users || res.finalResponse?.users;
  if (Array.isArray(accounts) && accounts.length > 0) {
    profile = { ...accounts[0] };
    console.log('[ABHA DEBUG] Found base profile in accounts/users array');
  }

  // 2. Scan candidates for more details to merge
  const candidates = [
    res.ABHAProfile,
    res.data?.ABHAProfile,
    res.data,
    res.finalResponse?.data,
    res.finalResponse,
    res
  ];

  for (const c of candidates) {
    if (!c || typeof c !== 'object' || Array.isArray(c)) continue;
    
    // Check if this candidate has meaningful profile data
    const hasInfo = 
      c.healthId || c.healthIdNumber || c.ABHANumber || c.abhaNumber ||
      c.name || c.fullName || c.firstName || c.gender || c.dob || c.dateOfBirth;
      
    if (hasInfo) {
      console.log('[ABHA DEBUG] Merging profile info from candidate');
      if (!profile) {
        profile = { ...c };
      } else {
        // Merge! We keep existing fields (preferring the one found in accounts array if overlap)
        // but fill in missing fields from this candidate
        profile = { ...c, ...profile };
      }
    }
  }

  if (profile) {
    // Ensure token is attached
    const existingToken = findToken(profile);
    if (!existingToken && globalToken) {
      profile.token = globalToken;
    }
    return profile;
  }

  // If no explicit profile info found, but we have a token, return at least that
  if (globalToken) {
    return { token: globalToken };
  }
  
  return null;
};

// ── Visual ABHA Card Component ───────────────────────────────────────────────
export const VisualAbhaCard = ({ profile, onCardLoad, txnId }) => {
  const [cardImage, setCardImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imgFailed, setImgFailed] = useState(false);
  const officialCardRef = useRef(null);

  // Capture OfficialAbhaCard via html2canvas so the Download button still works
  useEffect(() => {
    const isFallback = profile && (imgFailed || (!loading && !error && !cardImage));
    if (!isFallback || !onCardLoad) return;
    let cancelled = false;
    (async () => {
      await new Promise(r => setTimeout(r, 700));
      if (cancelled || !officialCardRef.current) return;
      try {
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(officialCardRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
        });
        if (!cancelled) onCardLoad(canvas.toDataURL('image/png'));
      } catch (e) {
        console.error('[ABHA DEBUG] html2canvas capture failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [imgFailed, loading, error, cardImage, profile, onCardLoad]);

  useEffect(() => {
    const fetchCard = async () => {
      let token = findToken(profile);
      
      // Secondary fallback to txnId if provided
      if (!token && txnId) {
        console.log('[ABHA DEBUG] No token found in profile, trying txnId as fallback');
        token = txnId;
      }

      if (!token) {
        console.warn('[ABHA DEBUG] No token found in profile object:', JSON.stringify(profile));
        setError('No authentication token found. Unable to fetch ABHA card image.');
        return;
      }
      
      console.log('[ABHA DEBUG] Calling getPngCard with token starting with:', token.substring(0, 10));
      
      setLoading(true);
      setError(null);
      try {
        const res = await abhaApi.getPngCard(token);
        console.log('[ABHA DEBUG] getPngCard response type:', typeof res);
        if (res && typeof res === 'object') {
           console.log('[ABHA DEBUG] getPngCard response keys:', Object.keys(res));
        } else if (typeof res === 'string') {
           console.log('[ABHA DEBUG] getPngCard response string sample:', res.substring(0, 50));
        }

        const processImageData = (data) => {
          if (!data) return null;

          // 1. If it's already a Data URL
          if (typeof data === 'string' && data.startsWith('data:image')) {
            console.log('[ABHA DEBUG] Identified existing Data URL');
            return data;
          }

          // 2. If it's a Base64 string
          if (typeof data === 'string' && data.length > 100) {
            // Strip whitespace and newlines
            const clean = data.replace(/\s/g, '');
            // Base64 regex - allow characters that might appear in padded or unpadded base64
            if (/^[A-Za-z0-9+/=]+$/.test(clean)) {
              // Extra check: if it starts with common PNG/JPG base64 markers
              // PNG: iVBOR...
              if (clean.startsWith('iVBOR') || clean.length > 1000) {
                console.log('[ABHA DEBUG] Identified Base64 string (len:', clean.length, ')');
                return `data:image/png;base64,${clean}`;
              }
            }
          }

          // 3. If it's raw binary data (string or array-like)
          try {
            let uint8 = null;
            if (typeof data === 'string') {
              console.log('[ABHA DEBUG] String data detected (len:', data.length, '), checking for binary content...');
              
              // Recover bytes. Note: if mis-encoded as UTF-8, 0x89 becomes 0xFFFD (65533)
              // We also check for 'PNG' which is common in corrupted data
              const buf = new Uint8Array(data.length);
              let replacementChars = 0;
              for (let i = 0; i < data.length; i++) {
                let code = data.charCodeAt(i);
                if (code === 65533) {
                  replacementChars++;
                  // Replacement character at index 0 is almost certainly 0x89 in a PNG
                  if (i === 0) {
                    buf[i] = 0x89;
                    continue;
                  }
                }
                buf[i] = code & 0xff;
              }
              
              // Verify PNG signature (89 50 4E 47)
              const hasPngSignature = (buf[0] === 0x89 && buf[1] === 0x50 && buf[2] === 0x4E && buf[3] === 0x47);
              const hasCommonMarkers = data.includes('PNG') || data.includes('IHDR') || data.includes('IDAT');
                                   
              if (hasPngSignature || (replacementChars > 0 && hasCommonMarkers)) {
                console.log('[ABHA DEBUG] Binary PNG detected via signature/markers. Replacement chars:', replacementChars);
                uint8 = buf;
              } else {
                console.log('[ABHA DEBUG] String does not appear to be valid binary PNG.');
              }
            } else if (data instanceof Uint8Array) {
              uint8 = data;
            } else if (Array.isArray(data) || (typeof data === 'object' && data[0] !== undefined)) {
              // Handle numeric object like {0: 137, 1: 80, ...}
              const keys = Object.keys(data);
              uint8 = new Uint8Array(keys.length);
              for (let i = 0; i < keys.length; i++) {
                const val = data[i];
                uint8[i] = typeof val === 'number' ? val : (parseInt(val) || 0);
              }
            }

            if (uint8 && uint8.length > 0) {
              console.log('[ABHA DEBUG] Recovered binary data. Length:', uint8.length);
              const blob = new Blob([uint8], { type: 'image/png' });
              return URL.createObjectURL(blob);
            }
          } catch (e) {
            console.error('[ABHA DEBUG] Binary processing failed:', e);
          }

          return null;
        };

        let imageSource = null;

        // Try extracting from common response structures
        if (res && res.data) {
          imageSource = processImageData(res.data);
        }
        
        if (!imageSource && res) {
          imageSource = processImageData(res.png || res.image || res.base64 || res);
        }

        if (imageSource) {
          console.log('[ABHA DEBUG] Final image source generated. Type:', imageSource.startsWith('data:') ? 'DataURL' : (imageSource.startsWith('blob:') ? 'BlobURL' : 'Unknown'));
          // If it's a blob URL, we might want to convert it to base64 for downloadAbhaCard
          if (imageSource.startsWith('blob:')) {
            const response = await fetch(imageSource);
            const blob = await response.blob();
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64data = reader.result;
              console.log('[ABHA DEBUG] Converted Blob to DataURL. Len:', base64data.length);
              setCardImage(base64data);
              if (onCardLoad) onCardLoad(base64data);
            };
            reader.readAsDataURL(blob);
          } else {
            console.log('[ABHA DEBUG] Setting card image directly. Len:', imageSource.length);
            setCardImage(imageSource);
            if (onCardLoad) onCardLoad(imageSource);
          }
        } else {
          console.error('[ABHA DEBUG] Could not parse image from response:', res);
          setError('Failed to process ABHA card image.');
        }
      } catch (err) {
        console.error('Failed to fetch ABHA PNG card:', err);
        setError(err.message || 'Failed to fetch official ABHA card image.');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [profile, txnId, onCardLoad]);

  if (loading) {
    return (
      <div style={{ height: 220, display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#f8fafc', borderRadius: 16, marginBottom: 24, border: '1px dashed #d9d9d9' }}>
        <Spin tip="Fetching official ABHA Card..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        title="Card Fetch Failed"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  // The official PNG from ABHA API has corrupted IDAT data (all non-ASCII bytes
  // replaced with U+FFFD). The <img> tag fires onError when it can't decode the
  // PNG. Fall back to DataAbhaCard which renders from the already-available profile.
  if (cardImage && !imgFailed) {
    return (
      <div style={{ marginBottom: 24, textAlign: 'center' }}>
        <img
          src={cardImage}
          alt="ABHA Card"
          style={{ width: '100%', borderRadius: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}
          onError={() => {
            console.warn('[ABHA DEBUG] Official PNG card failed to render (corrupted IDAT data). Falling back to profile card.');
            setImgFailed(true);
          }}
        />
      </div>
    );
  }

  // Fallback: render official-looking card from profile data when the ABHA API PNG is corrupted/unavailable
  if (imgFailed || (!loading && !error && !cardImage)) {
    if (profile) {
      return (
        <div style={{ marginBottom: 24 }}>
          <OfficialAbhaCard ref={officialCardRef} profile={profile} />
        </div>
      );
    }
  }

  return null;
};

// ── Data-only ABHA Card Component (for Find flow) ────────────────────────────
export const DataAbhaCard = ({ profile, hideGenderDob = false }) => {
  if (!profile) return null;

  const abhaNumber = profile.abhaNumber || profile.ABHANumber || profile.healthIdNumber || 'N/A';
  const abhaAddress = profile.abhaAddress || profile.preferredAbhaAddress || profile.healthId || 'N/A';
  const name = profile.fullName || profile.name || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || 'N/A';
  const status = profile.status || profile.verifiedStatus || 'ACTIVE';
  const isVerified = status.toLowerCase() === 'active' || status.toLowerCase() === 'verified';
  
  const mobileVerified = profile.mobileVerified === true || profile.mobileVerified === 'true' || profile.verifiedStatus === 'VERIFIED';
  
  const photo = profile.profilePhoto || profile.profilePhotoBytea || profile.image || profile.photo;
  const photoUrl = photo ? (photo.startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`) : null;

  return (
    <Card 
      style={{ 
        borderRadius: 20, 
        overflow: 'hidden', 
        border: '1px solid #e2e8f0',
        boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
        marginBottom: 24
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ 
        background: 'linear-gradient(135deg, #6B35D4 0%, #8B55F4 100%)', 
        padding: '20px', 
        color: 'white',
        textAlign: 'center'
      }}>
        <Avatar 
          size={100} 
          src={photoUrl} 
          icon={<UserOutlined />} 
          style={{ 
            border: '4px solid rgba(255,255,255,0.3)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            backgroundColor: '#f0f2f5'
          }}
        />
        <Title level={4} style={{ color: 'white', margin: '12px 0 0 0' }}>{name}</Title>
        <Tag color={isVerified ? 'success' : 'warning'} style={{ marginTop: 8, borderRadius: 12 }}>
          {status}
        </Tag>
      </div>
      
      <div style={{ padding: '24px' }}>
        <Flex vertical gap={16}>
          <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>ABHA NUMBER</Text>
            <Text strong style={{ fontSize: 18, letterSpacing: 1 }}>{abhaNumber}</Text>
          </div>
          
          <div style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>ABHA ADDRESS</Text>
            <Text strong style={{ fontSize: 16 }}>{abhaAddress}</Text>
          </div>

          <Flex justify="space-between" align="center">
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>MOBILE VERIFIED</Text>
              <Flex align="center" gap={4}>
                {mobileVerified ? (
                  <><CheckOutlined style={{ color: '#52c41a' }} /><Text strong>Verified</Text></>
                ) : (
                  <><CloseOutlined style={{ color: '#ff4d4f' }} /><Text strong>Not Verified</Text></>
                )}
              </Flex>
            </div>
            {!hideGenderDob && (
              <div style={{ textAlign: 'right' }}>
                <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>GENDER / DOB</Text>
                <Text strong>{profile.gender || 'N/A'} • {profile.dateOfBirth || profile.dob || 'N/A'}</Text>
              </div>
            )}
          </Flex>
        </Flex>
      </div>
    </Card>
  );
};

// ── Official ABHA Card (Government-style canvas-capturable card) ─────────────
export const OfficialAbhaCard = React.forwardRef(({ profile }, ref) => {
  if (!profile) return null;

  const abhaNumber = profile.abhaNumber || profile.ABHANumber || profile.healthIdNumber || '';
  const abhaAddress = profile.abhaAddress || profile.preferredAbhaAddress || profile.healthId
    || profile.phrAddress || profile.ABHAAddress || profile.abhaaddress || '';
  const name = profile.fullName || profile.name || `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || '';
  const gender = profile.gender || '';
  const dob = profile.dateOfBirth || profile.dob || '';
  const status = (profile.status || profile.verifiedStatus || 'ACTIVE').toUpperCase();
  const isActive = status === 'ACTIVE' || status === 'VERIFIED';
  const photo = profile.profilePhoto || profile.profilePhotoBytea || profile.image || profile.photo;
  const photoUrl = photo ? (photo.startsWith('data:') ? photo : `data:image/jpeg;base64,${photo}`) : null;
  const qrValue = abhaAddress || abhaNumber || 'ABHA';

  const formatAbhaNumber = (num) => {
    if (!num) return 'XX-XXXX-XXXX-XXXX';
    const clean = num.replace(/[^0-9]/g, '');
    if (clean.length === 14) {
      return `${clean.slice(0, 2)}-${clean.slice(2, 6)}-${clean.slice(6, 10)}-${clean.slice(10, 14)}`;
    }
    return num;
  };

  return (
    <div ref={ref} style={{ width: '100%', background: '#fff', borderRadius: 12, overflow: 'hidden', fontFamily: '"Segoe UI", Arial, sans-serif', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>

      {/* ── Header ── */}
      <div style={{ background: 'linear-gradient(90deg, #1A3761 0%, #2457A8 100%)', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ background: '#fff', borderRadius: 6, padding: '4px 8px', lineHeight: 1 }}>
            <span style={{ color: '#1A3761', fontWeight: 900, fontSize: 15, letterSpacing: 1 }}>ABHA</span>
          </div>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 12, lineHeight: 1.3 }}>Ayushman Bharat Health Account</div>
            <div style={{ color: '#a8c4e8', fontSize: 9 }}>National Health Authority · Govt. of India</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#fff', fontSize: 10, fontWeight: 600 }}>आयुष्मान भारत</div>
          <div style={{ color: '#a8c4e8', fontSize: 9 }}>स्वास्थ्य खाता</div>
        </div>
      </div>

      {/* ── Indian flag stripe ── */}
      <div style={{ height: 3, display: 'flex' }}>
        <div style={{ flex: 1, background: '#FF9933' }} />
        <div style={{ flex: 1, background: '#ffffff', borderTop: '1px solid #e8e8e8', borderBottom: '1px solid #e8e8e8' }} />
        <div style={{ flex: 1, background: '#138808' }} />
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex', padding: '16px', gap: '14px', background: '#EEF3FB', alignItems: 'flex-start' }}>

        {/* Photo + Status badge */}
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 78, height: 96, border: '2px solid #1A3761', borderRadius: 6, overflow: 'hidden', background: '#c8d8ef', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {photoUrl
              ? <img src={photoUrl} alt="Photo" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <UserOutlined style={{ fontSize: 30, color: '#7a9cc4' }} />}
          </div>
          <div style={{ background: isActive ? '#12B76A' : '#F79009', borderRadius: 4, padding: '2px 7px' }}>
            <span style={{ color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: 0.5 }}>{status}</span>
          </div>
        </div>

        {/* Info fields */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 8, color: '#5f7590', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1 }}>Name / नाम</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A3761', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || 'N/A'}</div>
          </div>

          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 8, color: '#5f7590', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1 }}>ABHA Number</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1A3761', letterSpacing: 1.5, fontFamily: 'monospace' }}>{formatAbhaNumber(abhaNumber)}</div>
          </div>

          <div style={{ display: 'flex', gap: 20, marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 8, color: '#5f7590', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1 }}>Date of Birth</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#333' }}>{dob || 'N/A'}</div>
            </div>
            <div>
              <div style={{ fontSize: 8, color: '#5f7590', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1 }}>Gender</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#333' }}>{gender || 'N/A'}</div>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 8, color: '#5f7590', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 1 }}>ABHA Address</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{abhaAddress || 'N/A'}</div>
          </div>
        </div>

        {/* QR Code */}
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ background: '#fff', padding: 6, borderRadius: 6, border: '1px solid #c8d8ef' }}>
            <QRCode value={qrValue} size={80} bordered={false} />
          </div>
          <div style={{ fontSize: 8, color: '#5f7590' }}>Scan to verify</div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ background: '#1A3761', padding: '7px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ color: '#a8c4e8', fontSize: 8 }}>Digitally generated · Visit abdm.gov.in to verify</div>
        <div style={{ color: '#fff', fontSize: 8, fontWeight: 700 }}>abdm.gov.in</div>
      </div>
    </div>
  );
});
OfficialAbhaCard.displayName = 'OfficialAbhaCard';

// ── OTP Input Component ──────────────────────────────────────────────────────
export const OtpInput = ({ value, onChange, length = 6 }) => {
  const refs = useRef([]);
  const digits = value ? value.split('') : [];

  const handleChange = (e, idx) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...Array(length)].map((_, i) => (i === idx ? val : digits[i] || ''));
    onChange(next.join(''));
    if (val && idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  };

  return (
    <div className="otp-container" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
      {[...Array(length)].map((_, idx) => (
        <Input
          key={idx}
          ref={(el) => (refs.current[idx] = el)}
          className="otp-input"
          maxLength={1}
          value={digits[idx] || ''}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          inputMode="numeric"
          style={{
            width: 48,
            height: 52,
            textAlign: 'center',
            fontSize: 20,
            fontWeight: 700,
            borderRadius: 8,
            border: '2px solid #e0d6ff',
            padding: 0,
          }}
        />
      ))}
    </div>
  );
};

export const downloadAbhaCard = (base64Card, profile) => {
  if (!base64Card) {
    message.error('Card image not loaded yet');
    return;
  }
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const img = new Image();
    img.src = base64Card;
    img.onload = () => {
      const imgWidth = 180;
      const imgHeight = (img.height * imgWidth) / img.width;
      pdf.addImage(base64Card, 'PNG', 15, 20, imgWidth, imgHeight);
      const fileName = profile?.healthIdNumber || profile?.ABHANumber || profile?.healthId || 'ABHA_Card';
      pdf.save(`ABHA_Card_${fileName}.pdf`);
    };
  } catch (err) {
    console.error('PDF generation error:', err);
    message.error('Failed to generate PDF');
  }
};
